import {
  interpolateString,
  SiteUrlsContext
} from '@tableau/data-catalog-shared';
import { SiteUrls } from '@tableau/shared-urls';
import {
  ButtonType,
  ButtonWidget,
  DialogPositioningBehavior,
  DialogType,
  DialogWidget,
  HorizontalOriginOption,
  OriginOptions,
  TextLinkWidget,
  VerticalOriginOption
} from '@tableau/widgets';

import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import { SvgInfo } from '../../../gen-src/icons/Info';

import {
  DescriptionTooltipText,
  fieldCardDetailTitleIconClass,
} from '../vizclient/VizClientFieldCardStyles';

// These types are based on types returned by multiple sorts of GraphQL queries, reduced to
// only those fields that are needed to display a description info widget.
export type DescriptionInfoTable = {
  id: string,
  name: string | null
}

export type DescriptionInfoEmbeddedDatasource = {
  id: string,
  name: string | null,
  __typename: 'EmbeddedDatasource',
}

export type DescriptionInfoPublishedDatasource = {
  id: string,
  name: string | null,
  vizportalUrlId: string,
  __typename: 'PublishedDatasource',
}

export type DescriptionInfoDatasource = DescriptionInfoPublishedDatasource | DescriptionInfoEmbeddedDatasource;

export type DescriptionInfoColumn = {
  id: string,
  table: DescriptionInfoTable | null,
  name: string | null
}

export type DescriptionInfoField = {
  id: string,
  name: string | null,
  datasource: DescriptionInfoDatasource | null,
  __typename: "DatasourceField"
  | "CalculatedField"
  | "BinField"
  | "SetField"
  | "CombinedField"
  | "CombinedSetField"
  | "GroupField"
  | "HierarchyField"
  | "ColumnField",
}

export type DescriptionInfoProps = {
  direction?: 'horizontal' | 'vertical',
  sourceColumn?: DescriptionInfoColumn,
  sourceField?: DescriptionInfoField,
}

/**
 * Displays a control if the field or column's description is inherited
 *
 * @param props The source column and/or field to be displayed
 * @returns An element representing the description info button
 */
export function DescriptionInfo(props: DescriptionInfoProps): JSX.Element | null {
  const column = props.sourceColumn;
  const field = props.sourceField;
  const direction = props.direction ?? 'vertical';

  const [isShown, setIsShown] = React.useState<boolean>(false);

  const buttonRef = React.useRef<HTMLButtonElement>(null);

  // for great accessibility, refocus the button when the dialog closes
  const closeDialog = () => {
    setIsShown(false);
    buttonRef.current?.focus();
  };

  let descriptionTooltipContent: JSX.Element | null = null;

  if (column && field) {
    descriptionTooltipContent = (
      <DescriptionTooltipText>
        {interpolateString(Messages.DescriptionInfoInheritedFromColumnThoughField(
          {
            column: '[COLUMN]',
            table: '[TABLE]',
            field: '[FIELD]',
            datasource: '[DATASOURCE]',
          }),
          {
            COLUMN: <ColumnLink key='column-link' column={column} />,
            TABLE: <TableLink key='table-link' table={column.table} />,
            FIELD: <FieldLink key='field-link' remoteField={field} />,
            DATASOURCE: <DatasourceLink key='datasource-link' remoteField={field} />,
          }
        )}
      </DescriptionTooltipText>
    );
  } else if (field) {
    descriptionTooltipContent = (
      <DescriptionTooltipText>
        {interpolateString(Messages.DescriptionInfoInheritedFromField(
          {
            field: '[FIELD]',
            datasource: '[DATASOURCE]',
          }),
          {
            FIELD: <FieldLink key='field-link' remoteField={field} />,
            DATASOURCE: <DatasourceLink key='field-link' remoteField={field} />,
          }
        )}
      </DescriptionTooltipText>
    );
  } else if (column) {
    descriptionTooltipContent = (
      <DescriptionTooltipText>
        {interpolateString(Messages.DescriptionInfoInheritedFromColumn(
          {
            column: '[COLUMN]',
            table: '[TABLE]',
          }),
          {
            COLUMN: <ColumnLink key='column-link' column={column} />,
            TABLE: <TableLink key='table-link' table={column.table} />,
          }
        )}
      </DescriptionTooltipText>
    );
  }

  // If we have nothing to display in the tooltip, do not display an info button.
  if (descriptionTooltipContent === null) {
    return null;
  }

  const itemId: string = (column ? column.id : field?.id) ?? ''; // Exactly one of column and field should be defined
  const anchorOrigin: OriginOptions = direction === 'horizontal'
    ? {
      vertical: VerticalOriginOption.centerY,
      horizontal: HorizontalOriginOption.right,
    }
    : {
      vertical: VerticalOriginOption.top,
      horizontal: HorizontalOriginOption.centerX,
    };

  return (
    <div style={{ display: 'inline-block' }}>
      <span className={fieldCardDetailTitleIconClass}>
        <ButtonWidget
          buttonType={ButtonType.Icon}
          buttonRef={buttonRef}
          onClick={() => setIsShown(true)}
          title={Messages.DescriptionInfo()}
          testId={'descriptionInfo-' + itemId}>
          <SvgInfo />
        </ButtonWidget>
      </span>
      {
        isShown && <DialogWidget
          anchorEl={buttonRef.current}
          anchorOrigin={anchorOrigin}
          dialogWidth={300}
          handleGlassClick={closeDialog}
          isGlassTransparent={true}
          isModal={true}
          onCloseRequest={closeDialog}
          positioningBehavior={DialogPositioningBehavior.Anchored}
          showCloseButton={false}
          testId={'descriptionInfo-' + itemId + '-dialog'}
          title='Description information'
          type={DialogType.Uber}
        >
          {descriptionTooltipContent}
        </DialogWidget>
      }
    </div>
  );
}

function ColumnLink(props: { column: DescriptionInfoColumn }): JSX.Element {
  return (
    <SiteUrlsContext.Consumer>
      {urls =>
        <TextLinkWidget
          testId='description-tooltip-column-link'
          href={maybeColumnUrl(props.column, urls)}
          target='_blank'
        >
          {props.column.name}
        </TextLinkWidget>
      }
    </SiteUrlsContext.Consumer>
  );
}

function maybeColumnUrl(column: DescriptionInfoColumn, urls: SiteUrls): string {
  return column.table
    ? urls.catalogTableColumnUrl(column.table.id, column.id)
    : '';
}

function TableLink(props: { table: DescriptionInfoTable | null }): JSX.Element {
  return (
    <SiteUrlsContext.Consumer>
      {urls =>
        <TextLinkWidget
          testId='description-tooltip-table-link'
          href={maybeTableUrl(props.table, urls)}
          target='_blank'
        >
          {props.table ? props.table.name : ''}
        </TextLinkWidget>
      }
    </SiteUrlsContext.Consumer>
  );
}

function maybeTableUrl(table: DescriptionInfoTable | null, urls: SiteUrls): string {
  return table
    ? urls.catalogTableUrl(table.id)
    : '';
}

function FieldLink(props: { remoteField: DescriptionInfoField }): JSX.Element {
  return (
    <SiteUrlsContext.Consumer>
      {urls =>
        <TextLinkWidget
          testId='description-tooltip-field-link'
          href={maybeFieldUrl(props.remoteField, urls)}
          target='_blank'
        >
          {props.remoteField.name}
        </TextLinkWidget>
      }
    </SiteUrlsContext.Consumer>
  );
}

function maybeFieldUrl(remoteField: DescriptionInfoField, urls: SiteUrls): string {
  return remoteField.datasource && remoteField.datasource.__typename === 'PublishedDatasource'
    ? urls.datasourceLineageFieldUrl(remoteField.datasource.vizportalUrlId, remoteField.id)
    : '';
}

function DatasourceLink(props: { remoteField: DescriptionInfoField }): JSX.Element {
  return (
    <SiteUrlsContext.Consumer>
      {urls =>
        <TextLinkWidget
          testId='description-tooltip-datasource-link'
          href={maybeDatasourceUrl(props.remoteField, urls)}
          target='_blank'
        >
          {props.remoteField.datasource?.name || ''}
        </TextLinkWidget>
      }
    </SiteUrlsContext.Consumer>
  );
}

function maybeDatasourceUrl(remoteField: DescriptionInfoField, urls: SiteUrls): string {
  return remoteField.datasource && remoteField.datasource.__typename === 'PublishedDatasource'
    ? urls.datasourceLineageUrl(remoteField.datasource.vizportalUrlId)
    : '';
}

type InheritedFromTableType = {
  __typename: string;
  id: string;
  name: string | null;
}

type InheritedFromDatabaseType = InheritedFromEmbeddedDatabaseType | InheritedFromPublishedDatasourceType;

type InheritedFromEmbeddedDatabaseType = {
  __typename: "EmbeddedDatasource";
  id: string;
  name: string | null;
}

type InheritedFromPublishedDatasourceType = {
  __typename: "PublishedDatasource";
  id: string;
  name: string | null;
  vizportalUrlId: string;
}

type InheritedFromAssetType = InheritedFromAssetFieldType | InheritedFromAssetColumnType;

type InheritedFromAssetFieldType = {
  __typename: "CalculatedField"
  | "BinField"
  | "SetField"
  | "CombinedField"
  | "CombinedSetField"
  | "DatasourceField" | "GroupField" | "HierarchyField" | "ColumnField";
  id: string;
  name: string | null;
  datasource: InheritedFromDatabaseType | null;
}

type InheritedFromAssetColumnType = {
  __typename: 'Column';
  id: string;
  name: string | null;
  table: InheritedFromTableType | null;
};

export type DescriptionInheritedType = {
  __typename: "InheritedStringResult";
  assetId: string;
  attribute: string;
  value: string | null;
  distance: number | null;
  asset: InheritedFromAssetType | null;
};

export type RowDataType = {
  readonly description?: string | null;
  readonly descriptionInherited?: (DescriptionInheritedType | null)[] | null;
}

export function DescriptionInheritedFromRowData(rowData: RowDataType): DescriptionInheritedType | null {
  const descriptionInherited = rowData.descriptionInherited;

  // Gather the nearest defined, non-null descriptionInherited entry, if any.
  if (descriptionInherited == null || descriptionInherited.length === 0) {
    return null;
  }

  // Find an element with the lowest distance.
  const descriptionsInherited = descriptionInherited
    .filter((value) => value?.attribute === 'description');

  // Treat null- and undefined-distance items as very distant
  const maxDistance = 9999;
  const nearestElement = descriptionsInherited.sort((a, b) => {
    let aDistance = a?.distance;
    let bDistance = b?.distance;

    if (aDistance == null) {
      aDistance = maxDistance;
    }

    if (bDistance == null) {
      bDistance = maxDistance;
    }

    return aDistance - bDistance;
  })[0];

  // If no valid elements found, abort
  if (nearestElement === null) {
    return null;
  }

  // Find elements with the same distance. We should have exactly 1.
  const nearestElementsCount = descriptionsInherited
    .filter((value) => value?.distance === nearestElement?.distance).length;

  // If there are multiple elements tied as the closest, behave as if there were no inheritance
  if (nearestElementsCount !== 1) {
    return null;
  }

  return nearestElement;
}

export function DescriptionIconFromRowData(rowData: RowDataType): JSX.Element | null {
  // We would only possibly display an icon if descriptionInherited is defined
  // and if a local description is not present.
  const descriptionInherited = DescriptionInheritedFromRowData(rowData);

  // Display no icon if a local description is present
  if (rowData.description != null) {
    return null;
  }

  if (descriptionInherited === null) {
    return null;
  }

  if (descriptionInherited.asset === null) {
    return null;
  }

  // We can have inheritance from a field or from a column
  if (descriptionInherited.asset.__typename === 'Column') {
    const inheritedAssetColumn: DescriptionInfoColumn = {
      id: descriptionInherited.asset.id,
      name: descriptionInherited.asset.name,
      table: descriptionInherited.asset.table,
    };

    return <DescriptionInfo sourceColumn={inheritedAssetColumn} />;
  }

  if (descriptionInherited.asset.__typename.endsWith('Field')) {
    const inheritedAssetField: DescriptionInfoField = {
      id: descriptionInherited.asset.id,
      name: descriptionInherited.asset.name,
      datasource: descriptionInherited.asset.datasource,
      __typename: descriptionInherited.asset.__typename,
    };

    return <DescriptionInfo sourceField={inheritedAssetField} />
  }

  return null; // Should only happen if neither a column nor a field is the source of inheritance
}

export function DescriptionTextFromRowData(rowData: RowDataType): string | null {
  const description = rowData.description;
  const descriptionInherited = DescriptionInheritedFromRowData(rowData);

  // Only one of these should be defined. `description` depending on which query has been executed.
  if (description != null) {
    return description;
  }

  if (descriptionInherited !== null) {
    return descriptionInherited.value;
  }

  return null; // Should only happen if row data is malformed
}
